// Extension is explicit so this module also loads as a native ES module
// outside the esbuild bundle, which is what lets the placement maths be
// exercised directly in a browser harness.
import {
  computeFixedPlacement,
  computePlacement,
  intersectBounds,
  GAP,
} from "./dropdown-placement.js"

// Keeps a roster action menu inside the visible area (#477).
//
// These menus are absolutely positioned inside the cap sheet's
// `.overflow-x-auto` table wrapper. Setting `overflow-x: auto` forces the
// computed `overflow-y` to `auto` as well, so the wrapper clips vertically
// even though nothing asked it to: a menu opened on one of the last rows is
// cut off at the table's bottom edge and is effectively unusable.
//
// The panel is LiveView-managed DOM, so it can't be relocated to <body> the
// way the hover tooltips do it (LiveView would lose track of the subtree).
// Instead it stays put and we pick the side with room: flip above the trigger
// when it doesn't fit below, and cap the height to whatever space is actually
// left so the menu scrolls internally rather than disappearing.
//
// Clipping is cumulative, so every scrollable/hidden ancestor is intersected
// with the viewport — an inner scroller can't grant room an outer modal or the
// window edge has already taken away.

// Ancestors that clip, from nearest outward. `position: fixed` escapes any
// non-transformed scroll container, so the walk stops there.
function clippingRects(el) {
  const rects = []

  for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
    const style = getComputedStyle(node)
    if (/(auto|scroll|hidden|clip)/.test(style.overflowX + style.overflowY)) {
      rects.push(node.getBoundingClientRect())
    }
    if (style.position === "fixed") break
  }

  return rects
}

// A fixed dropdown can opt into a sticky element that occludes the top of the
// viewport. The banner remains the higher visual layer; the dropdown treats its
// visible bottom edge as the usable viewport's top instead of rendering behind
// it. Missing, off-screen, or malformed selectors safely fall back to zero.
function fixedViewportTop(el) {
  const selector = el.dataset?.dropdownTopBoundary
  if (!selector) return 0

  let boundary

  try {
    boundary = document.querySelector(selector)
  } catch (_error) {
    return 0
  }

  if (!boundary) return 0

  const rect = boundary.getBoundingClientRect()
  if (rect.bottom <= 0 || rect.top >= window.innerHeight) return 0

  return Math.max(0, Math.min(rect.bottom, window.innerHeight))
}

export default {
  mounted() {
    // A capped panel scrolls internally, and the capture-phase listener sees
    // that scroll too. Re-measuring would clear maxHeight/overflowY to read the
    // natural size, which collapses the scroll range and snaps scrollTop back
    // to 0 — so the lower actions became unreachable. Only ancestor scrolling
    // actually moves the panel, so ignore the panel's own.
    this.reposition = (event) => {
      if (event && event.target === this.el) return
      this.position()
    }

    // Placement goes stale the moment anything scrolls under it. Capture phase
    // so inner scrollers are seen too; passive since we never preventDefault.
    window.addEventListener("scroll", this.reposition, { capture: true, passive: true })
    window.addEventListener("resize", this.reposition, { passive: true })
    this.position()
  },

  updated() {
    this.position()
  },

  destroyed() {
    window.removeEventListener("scroll", this.reposition, { capture: true })
    window.removeEventListener("resize", this.reposition)
  },

  position() {
    if (this.el.dataset?.dropdownPosition === "fixed") {
      this.positionFixed()
      return
    }

    this.positionAbsolute()
  },

  positionFixed() {
    const el = this.el
    const anchor = document.getElementById(el.dataset.dropdownAnchor)
    if (!anchor) return

    const previousScrollTop = el.scrollTop

    // Measure the complete result list, not a previous viewport cap or the
    // panel's old absolute position inside the rotation scroller.
    el.style.position = "fixed"
    el.style.top = "0"
    el.style.bottom = "auto"
    el.style.left = "0"
    el.style.width = ""
    el.style.marginTop = "0"
    el.style.marginBottom = "0"
    el.style.maxHeight = ""
    el.style.overflowY = ""

    const anchorRect = anchor.getBoundingClientRect()
    const panelHeight = el.scrollHeight || el.getBoundingClientRect().height
    const { maxHeight, top } = computeFixedPlacement({
      anchorTop: anchorRect.top,
      anchorBottom: anchorRect.bottom,
      panelHeight,
      viewportTop: fixedViewportTop(el),
      viewportBottom: window.innerHeight,
    })

    // Inline fixed geometry wins over the panel's reusable Tailwind classes
    // while the LiveView-owned node remains exactly where it was rendered.
    el.style.left = `${anchorRect.left}px`
    el.style.top = `${top}px`
    el.style.width = `${anchorRect.width}px`

    if (maxHeight !== null) {
      el.style.maxHeight = `${maxHeight}px`
      el.style.overflowY = "auto"
      el.scrollTop = previousScrollTop
    }
  },

  positionAbsolute() {
    const el = this.el

    // Dropping the height cap below un-scrolls the panel, so remember where the
    // user had scrolled to and put it back once the cap is reapplied.
    const previousScrollTop = el.scrollTop

    // Clear previous placement so the natural size and position are what we
    // measure, rather than whatever the last pass left behind.
    el.style.top = ""
    el.style.bottom = ""
    el.style.marginBottom = ""
    el.style.maxHeight = ""
    el.style.overflowY = ""

    // The panel is positioned against its `.relative` wrapper, which sits in
    // the table cell — that's the trigger we flip around.
    const anchor = el.offsetParent
    if (!anchor) return

    const bounds = intersectBounds([
      ...clippingRects(el),
      { top: 0, bottom: window.innerHeight },
    ])

    const anchorRect = anchor.getBoundingClientRect()
    const panelRect = el.getBoundingClientRect()

    const { placement, maxHeight, offsetTop } = computePlacement({
      anchorTop: anchorRect.top,
      naturalTop: panelRect.top,
      panelHeight: panelRect.height,
      boundsTop: bounds.top,
      boundsBottom: bounds.bottom,
    })

    if (placement === "above") {
      el.style.top = "auto"
      el.style.bottom = "100%"
      el.style.marginBottom = `${GAP}px`
    } else if (placement === "fill") {
      // Too tight either way: span the whole visible region instead of
      // squeezing into a sliver. offsetTop is viewport-relative, so rebase it
      // onto the offset parent the panel is positioned against.
      el.style.top = `${offsetTop - anchorRect.top}px`
    }

    if (maxHeight !== null) {
      el.style.maxHeight = `${maxHeight}px`
      el.style.overflowY = "auto"
      el.scrollTop = previousScrollTop
    }
  },
}
